import type { AppUserData } from "@glide/common-core/dist/js/components/datastore/data-row-store";
import {
    type GroundValue,
    type LoadingValue,
    type Row,
    type WritableValue,
    isLoadingValue,
    makeLoadingValue,
    MutableTable,
    Table,
    makeLoadingValueWithDisplayValue,
    unwrapLoadingValue,
    type QueryTableVersions,
    type RowIndex,
    type SerializedQuery,
    Query,
    glideTableInGBTRowLimit,
} from "@glide/computation-model-types";
import {
    type ActionAppEnvironment,
    type QueryableDataStore,
    type QueryableRowsRootFinder,
} from "@glide/common-core/dist/js/components/types";
import { isTable } from "@glide/common-core/dist/js/computation-model/data";
import { decomposeRowIndex } from "@glide/common-core/dist/js/Database";
import {
    type TableName,
    nativeTableRowIDColumnName,
    type TableGlideType,
    isComputedColumn,
    makeTypeSchema,
    tableGlideTypeCodecTableToTableGlideType,
    isGlideTableInGBTDataStore,
    nativeTableRowDeletedColumnName,
} from "@glide/type-schema";
import { getFeatureSetting } from "@glide/common-core/dist/js/feature-settings";
import {
    type BuilderQueryInfo,
    type ExecuteQueryResponseBody,
    type MinRequiredVersion,
    type NativeTableQueryID,
} from "@glide/common-core/dist/js/firebase-function-types";
import {
    type ChangeObservable,
    type JSONObject,
    ArrayMap,
    concatIterableIterators,
    ConditionVariable,
    iterableFilter,
    iterableTake,
    logError,
    logInfo,
    nativeTableIndexer,
    RecurrentBackgroundJob,
    Watchable,
} from "@glide/support";
import {
    applySort,
    areConditionsTrueForRow,
    areSerializedQueriesEquivalent,
    doesQueryReferToColumns,
    getSerializedQueryDistance,
} from "@glide/query-conditions";
import {
    assert,
    assertNever,
    defined,
    definedMap,
    filterUndefined,
    mapFilterUndefined,
} from "@glideapps/ts-necessities";
import { setUnionInto } from "collection-utils";
import { ComputationModelDataRowStoreBase } from "./data-row-store";
import { RowChangeOverlayer } from "./row-change-overlayer";
import { getColumnsAffectedByDataColumns } from "@glide/generator/dist/js/computed-columns";
import { makeSimpleSchemaInspector } from "@glide/generator/dist/js/components/simple-ccc";
import { getColumnsUsedByQuery } from "./columns-used-by-query";
import {
    type AggregateQueryAggregatorHelper,
    AggregateQueryAggregator,
    makeQueryRequest,
} from "./aggregate-query-aggregator";
import { getLocalizedString } from "@glide/localization";
import {
    type ActiveAggregationQuery,
    type ActiveQuery,
    type ActiveQueryBase,
    QueryState,
    setTableVersionsForQuery,
} from "./query/active-query";
import { type BuildQueryRefresher, type QueryRefresher, maxErroredQueryRetries } from "./query/query-refresher";
import type {
    ExistingQueryRowDataStore,
    ExistingQueryRowDataStoreCallbacks,
    QueryableBackendCaller,
} from "./queryable-data-store-types";
import { RowVersions } from "./row-versions";
import {
    adjustRequeryLimit,
    decomposeRowIDQuery,
    getRowIndex,
    makeRowFromRowData,
    makeRowIDForRow,
    unwrapLoadingTable,
} from "./queryable-data-store-support";
import { ErrorInfo, isErrorInfo, type ErrorOr } from "@glide/error-info";
import { extractActionValues } from "@glide/common-core/dist/js/computation-model/row-data";

export { glideTableInGBTRowLimit };
export const existingQueryExecutionDelay = 100;

const queryBatchSize = 10; // FIXME: See if this can go higher
// FIXME: Longer?
const defaultContinuationExpiration = 60_000; // 1 minute
// Adding this many rows since the last GC will trigger another one.
const numRowsToTriggerGC = 5000;

function runAndClearCallbacks(q: ActiveQueryBase): void {
    // just to be super safe
    const callbacks = Array.from(q.callbacks);
    q.callbacks.clear();

    for (const cb of callbacks) {
        try {
            cb();
        } catch (e: unknown) {
            logError("Error calling query callback", e);
        }
    }
}

function unlimitSerializedQuery(serializedQuery: SerializedQuery): SerializedQuery {
    const result = { ...serializedQuery };
    delete result["limit"];
    return result;
}

function getRowsToAdd(responseRows: readonly unknown[]): readonly JSONObject[] {
    return responseRows.filter(r => r !== null && typeof r === "object") as JSONObject[];
}

let nextDummyJobIndex = 1;
function makeDummyJobID(): string {
    const id = `dummy-${nextDummyJobIndex}`;
    nextDummyJobIndex++;
    return id;
}

export class ExistingQueryRowDataStoreImpl
    extends ComputationModelDataRowStoreBase
    implements ExistingQueryRowDataStore
{
    // private _queryQueue = new SyncJobQueue();
    private readonly _activeQueries = new ArrayMap<SerializedQuery, ActiveQuery>(areSerializedQueriesEquivalent);
    private readonly _activeAggregationQueries = new ArrayMap<SerializedQuery, ActiveAggregationQuery>(
        areSerializedQueriesEquivalent
    );
    private readonly _invalidatedAggregateQueries = new ArrayMap<SerializedQuery, MutableTable>(
        areSerializedQueriesEquivalent
    );
    private readonly _aggregateAggregator: AggregateQueryAggregator;
    // This exists for the benefit of the builder, to handle modified query definitions
    // for existing queries.
    private _saveSerial = 0;
    private _numRowsAddedSinceGC = 0;

    private _activeQueriesCV = new ConditionVariable();
    private _builderInfo: true | BuilderQueryInfo | undefined;

    private readonly _queriesRunningWatchable = new Watchable(false);

    private readonly _rowChangeOverlayer = new RowChangeOverlayer();

    // job ID -> column names | true (for all columns)
    private readonly _columnsAffectedByJob = new Map<string, ReadonlySet<string> | true>();

    private readonly _queryRefresher: QueryRefresher;
    private readonly _rowVersions: RowVersions = new RowVersions();

    constructor(
        private _appEnvironment: ActionAppEnvironment,
        private readonly _tableName: TableName,
        private _table: TableGlideType | undefined,
        private readonly _savedID: NativeTableQueryID,
        /**
         * The latest version we've received from the backend, which is the
         * minimum version we demand when doing queries.
         */
        private _latestReceivedVersion: MinRequiredVersion | undefined,
        _buildQueryRefresher: BuildQueryRefresher,
        private readonly _backendCaller: QueryableBackendCaller,
        private readonly _callbacks: QueryableRowsRootFinder & ExistingQueryRowDataStoreCallbacks,
        /**
         * If a query requests a specific row by row ID, and we have that row,
         * don't even bother running the query.  We use this in Automations,
         * where each loop iteration will request the row for the current
         * item. In the frontend we explicitly query for a row when we enter a
         * screen for it, to make sure it's up-to-date, so we don't want that
         * there, though maybe with live-updates we don't even need to run the
         * query there anymore?
         */
        private readonly _shortCircuitRowIDQueries: boolean
    ) {
        super(false, false);

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const dataStore = this;
        const helper: AggregateQueryAggregatorHelper = {
            get appEnvironment(): ActionAppEnvironment {
                return dataStore._appEnvironment;
            },
            get backendCaller(): QueryableBackendCaller {
                return dataStore._backendCaller;
            },
            get nativeTableID(): NativeTableQueryID {
                return dataStore._savedID;
            },
            get builderInfo(): true | BuilderQueryInfo | undefined {
                return dataStore._builderInfo;
            },
            get minVersion(): MinRequiredVersion | undefined {
                return dataStore._latestReceivedVersion;
            },
        };
        this._aggregateAggregator = new AggregateQueryAggregator(helper);

        this._queryRefresher = _buildQueryRefresher(
            () => concatIterableIterators(this._activeAggregationQueries.values(), this._activeQueries.values()),
            () => this.handleQueriesRefreshed()
        );
    }

    private handleQueriesRefreshed(): void {
        this._activeQueriesCV.notifyAll();
        this._serviceNewQueriesJob.request();
        this._serviceNewAggregationsJob.request();
    }

    /** @deprecated This is only for testing */
    public get numInvalidatedAggregateQueries(): number {
        return this._invalidatedAggregateQueries.size;
    }

    public setBuilderInfo(builderInfo: true | BuilderQueryInfo | undefined) {
        this._builderInfo = builderInfo;
    }

    public setTable(table: TableGlideType): void {
        const computedColumns = new Set(
            mapFilterUndefined(this._table?.columns ?? [], c => (isComputedColumn(c) ? c.name : undefined))
        );

        this._table = table;

        this.invalidateQueriesAffectedByColumns(this._activeQueries, computedColumns, undefined);
        this.invalidateQueriesAffectedByColumns(this._activeAggregationQueries, computedColumns, undefined);

        // We clear the invalidated queries because they might not even
        // reflect the current schema anymore.
        this._invalidatedAggregateQueries.clear();
    }

    private updateLatestReceivedVersion(newVersion: number | undefined): void {
        if (newVersion === undefined) return;
        if (typeof this._latestReceivedVersion !== "number" || newVersion > this._latestReceivedVersion) {
            this._latestReceivedVersion = newVersion;
        }
    }

    public get areSubscribedQueriesRunning(): ChangeObservable<boolean> {
        return this._queriesRunningWatchable;
    }

    private updateQueriesRunningWatchable(): void {
        for (const q of concatIterableIterators(
            this._activeQueries.values(),
            this._activeAggregationQueries.values()
        )) {
            if (!this.isRelevantRunningQuery(q)) continue;
            this._queriesRunningWatchable.current = true;
            return;
        }
        this._queriesRunningWatchable.current = false;
    }

    private isRelevantRunningQuery(q: ActiveQueryBase): boolean {
        if (q.state === QueryState.Done) return false;

        // Don't show the loading indicator for reloading queries.
        if (q.lastValue !== undefined) return false;

        // We have to check whether there are callbacks, because queries
        // that don't have callbacks won't inform the backend that
        // anything changed, which means it can get stuck in a perpetual
        // "loading" state.  The alternative would be to make "queries
        // running" into an observable.
        if (q.callbacks.size === 0) return false;

        return true;
    }

    private getActiveQuery(subQuery: SerializedQuery): ActiveQuery | undefined {
        return this._activeQueries.get(unlimitSerializedQuery(subQuery));
    }

    private getClosestResolvedOrInvalidatedQuery(
        subQuery: SerializedQuery,
        activeQueries: ArrayMap<SerializedQuery, ActiveQueryBase>
    ): LoadingValue | undefined {
        const invalidated = this._invalidatedAggregateQueries.get(subQuery);
        if (invalidated !== undefined) {
            return makeLoadingValueWithDisplayValue(invalidated);
        }

        let closest: MutableTable | undefined;
        let closestDistance = Number.MAX_SAFE_INTEGER;
        for (const active of activeQueries.values()) {
            if (isLoadingValue(active.currentValue)) continue;

            const distance = getSerializedQueryDistance(subQuery, active.subQuery);
            if (distance === false || distance === 0) continue;
            if (distance < closestDistance) {
                closestDistance = distance;
                closest = active.currentValue;
            }
        }
        if (closestDistance > 10) return undefined;

        return makeLoadingValueWithDisplayValue(closest);
    }

    // NOTE: We're not calling `updateQueriesRunningWatchable` in these two
    // methods because the code paths they're in do eventually call it.  If
    // that changes we might have to call from here, too.
    private setActiveQuery(subQuery: SerializedQuery, active: ActiveQuery) {
        this._activeQueries.set(unlimitSerializedQuery(subQuery), active);
    }
    private deleteActiveQuery(subQuery: SerializedQuery) {
        this._activeQueries.delete(unlimitSerializedQuery(subQuery));
    }

    public reportSave() {
        this._saveSerial++;
        this.pushDirtForTable();
    }

    public recompute(): GroundValue {
        const result = super.recompute();

        // The above can return a dummy Table for the user profile table, so we need to run this
        //  `if` block regardless of what that returns.
        if (getFeatureSetting("glideTablesOnGBT") && isGlideTableInGBTDataStore(this.getTable()?.sourceMetadata)) {
            // FIXME: 25k rows is the documented limit for a Glide Table, but I worry that much data
            //  might still cause our executeQuery pods to OOM. We should instead load in smaller batches.
            const query = forceLoadQueryableTable(
                this._tableName,
                this,
                glideTableInGBTRowLimit,
                this._builderInfo === true
            );
            if (isLoadingValue(result)) {
                return query;
            }
        }

        // FIXME: Returning `new Table()` is a hack so that we can show screens that have
        // this table as its source.  We should do a proper query in those cases.
        return isLoadingValue(result) ? new Table() : result;
    }

    protected getAppUserData(): AppUserData | undefined {
        return undefined;
    }

    protected getTable(): TableGlideType | undefined {
        return this._table;
    }

    protected fetchData(): void {
        // Not doing anything - this needs to be queries explicitly
    }

    // This must only be called if `active.currentValue` is not loading
    private sortRowsIfNecessary(active: ActiveQuery): void {
        if (!active.updateLocally) return;

        if (isLoadingValue(active.currentValue)) return;

        const columns = this._table?.columns ?? [];
        active.currentValue.mutateAsArray(rows => {
            applySort(columns, active.subQuery.sort, rows);
        });
    }

    private async waitForFullActiveQuerySet(targetState: QueryState, signal: AbortSignal): Promise<ActiveQuery[]> {
        if (signal.aborted) return [];
        while (true) {
            const rightNow = new Date();
            // Before we get the active query set, let's reset all expired continuations that have no callbacks.
            const removeTargets = Array.from(
                iterableFilter(
                    this._activeQueries.values(),
                    e =>
                        e.state === QueryState.Continue &&
                        e.callbacks.size === 0 &&
                        e.nextContinuation?.expiresAfter !== undefined &&
                        e.nextContinuation.expiresAfter < rightNow
                )
            );
            for (const target of removeTargets) {
                target.state = QueryState.Execute;
                target.nextContinuation = undefined;
                target.tableVersions = undefined;
            }

            const targets = iterableTake(
                iterableFilter(this._activeQueries.values(), e => e.state === targetState && e.callbacks.size > 0),
                queryBatchSize
            );
            if (targets.length >= queryBatchSize || signal.aborted) {
                for (const target of targets) {
                    // As silly as this seems, there's a point to it.
                    // ArrayMaps preserve insertion order when iterated, similar to the
                    // guaranteed behavior of Map in ECMAScript 6. This allows us to treat
                    // an ArrayMap like a queue in certain circumstances. What we're
                    // effectively doing here is "resetting" the queue for these entries,
                    // to enforce some degree of fairness.
                    this.deleteActiveQuery(target.subQuery);
                    this.setActiveQuery(target.subQuery, target);
                }
                if (targets.length > 0) {
                    this._activeQueriesCV.notifyAll();
                }
                this.updateQueriesRunningWatchable();
                return targets;
            }
            this.updateQueriesRunningWatchable();
            await this._activeQueriesCV.wait();
        }
    }

    private gcIfNecessary(): void {
        if (this._numRowsAddedSinceGC < numRowsToTriggerGC) return;
        if (!this._callbacks.isReadyForGC) {
            logInfo("not ready for GC");
            return;
        }

        this._callbacks.resetReadyForGC();

        const rootRowIDs = Array.from(this._callbacks.getRowIDsForTable(this._tableName));
        const callbacksToRun: (() => void)[] = [];
        const unusedQueries: SerializedQuery[] = [];

        for (const [serialized, active] of this._activeQueries.entries()) {
            if (active.callbacks.size > 0) {
                logInfo("query has callbacks", active);
                if (active.state === QueryState.Done) {
                    // We run these again so that listeners have to decide
                    // whether they still need them.  Without doing this the
                    // callbacks would hang around forever.
                    callbacksToRun.push(...active.callbacks);
                    active.callbacks.clear();
                }
                continue;
            }
            if (isLoadingValue(active.currentValue)) {
                logInfo("query is loading", active);
                continue;
            }
            const table = active.currentValue;
            if (rootRowIDs.some(id => table.has(id))) {
                logInfo("query has root row", active);
                continue;
            }

            logInfo("query is unused", active);
            unusedQueries.push(serialized);
        }

        this._numRowsAddedSinceGC = 0;

        // If we haven't found any unused queries, there's no point trying to
        // delete rows.
        logInfo("unused queries", unusedQueries.length);
        if (unusedQueries.length > 0) {
            // First we delete all unused queries
            for (const serialized of unusedQueries) {
                this._activeQueries.delete(serialized);
            }

            // Then we get all the rows we currently have.
            const unusedRowIDs = new Set(this.tableData.keys());

            // From that list we remove all the rows that are still in use.
            for (const active of this._activeQueries.values()) {
                const table = active.currentValue;
                if (isLoadingValue(table)) continue;

                for (const rowID of table.keys()) {
                    unusedRowIDs.delete(rowID);
                }
            }

            // All the rows left in the list are not used anymore, so we
            // delete them.
            logInfo("unused rows", unusedRowIDs.size);
            for (const rowID of unusedRowIDs) {
                // We don't push dirt for these because they shouldn't be in
                // use anywhere.
                this.tableData.delete(rowID);
            }
        }

        this.updateQueriesRunningWatchable();

        for (const cb of callbacksToRun) {
            cb();
        }
    }

    private scheduleGC(): void {
        // We call this right after servicing queries, which is a good point
        // to schedule stuff because it's what happens regularly when the data
        // store does actual work, but it's almost the worst time to do a GC
        // because we've just called and cleared out all callbacks and not
        // given whoever listened to them a chance to resubscribe, so if we
        // did a GC then it's likely we would collect a bunch of queries that
        // are just about to be subscribed to again.  So we wait a bit.
        setTimeout(() => this.gcIfNecessary(), 10);
    }

    private finishQuery(q: ActiveQueryBase): void {
        q.state = QueryState.Done;

        let error = q.error;
        if (
            error !== undefined &&
            !this._callbacks.isBuilder &&
            !getFeatureSetting("sqlShowDetailedErrorMessageInPlayer")
        ) {
            error = new ErrorInfo(error.status, getLocalizedString("error"));
        }

        if (isLoadingValue(q.currentValue)) {
            if (error !== undefined) {
                q.currentValue = new MutableTable(undefined, error);
            }
        } else {
            q.currentValue = q.currentValue.withError(error);
        }
        runAndClearCallbacks(q);

        if (q.warningMessage !== undefined) {
            logError(`Warning running query ${this._savedID}`, q.warningMessage);
        }
        if (q.error !== undefined) {
            logError(`Error running query ${this._savedID}`, q.error.status, q.error.message);
            // FIXME: Do we signal the error to the user?  Do we put it back
            // into loading state?
        }

        this.onAfterQuery(q);
    }

    // `targetState` is the state of the queries this should run
    private async serviceQueriesInternal(
        targetState: QueryState,
        makeRequest: (targets: readonly ActiveQuery[]) => Promise<ErrorOr<ExecuteQueryResponseBody>>
    ) {
        const controller = new AbortController();
        const timeout = setTimeout(() => {
            controller.abort();
            this._activeQueriesCV.notifyAll();
        }, existingQueryExecutionDelay);
        const targets = await this.waitForFullActiveQuerySet(targetState, controller.signal);
        clearTimeout(timeout);

        // There's no need to make a network call if there are no requests to
        // actually run.
        if (targets.length === 0) {
            // We might still have queries that haven't even started running.
            if (targetState === QueryState.Continue) {
                this._serviceNewQueriesJob.request();
            }
            return;
        }

        // FIXME: Allow query calls to be aborted independent of the above timeout
        const queriesByID = new Map(targets.map(t => [t.subQueryID, t]));
        let firstQueryBody = await makeRequest(targets);

        if (isErrorInfo(firstQueryBody)) {
            const treat403AsEmpty =
                firstQueryBody.status === 403 &&
                this._table !== undefined &&
                getFeatureSetting("gbtTreat403AsEmpty");
            // A 403 on first login is usually transient: the app-user access
            // record isn't provisioned yet (see the errored-query retry in
            // `query-refresher`). When the retry is enabled we surface the 403
            // as a retriable error instead of masking it as a terminal empty
            // result, so the freshener re-runs it and the screen recovers
            // without a full reload. Once a query has exhausted its bounded
            // retries we settle it to the treat-as-empty behavior, so a
            // genuinely-forbidden query still settles to the empty result
            // `gbtTreat403AsEmpty` promises. This is decided per target below,
            // so in a mixed batch an exhausted target settles to empty even
            // while a fresh one alongside it is still being retried.
            //
            // Only retry when a refresher that actually re-runs errored
            // queries exists (`GbtQueryFreshener`). Without one (a
            // `NoOpQueryRefresher` — `gbtLiveUpdates` off, or automations)
            // nothing ever retries and `errorRetryCount` stays at 0 forever, so
            // the exhaustion fallback would never fire and the 403 would be
            // stuck as a permanent error instead of an empty result.
            if (!treat403AsEmpty) {
                // FIXME: Shouldn't we be sending a signal to consumers somehow?
                for (const target of targets) {
                    target.error = firstQueryBody;
                    this.finishQuery(target);
                }
                return;
            }

            const canRetryErroredQueries =
                getFeatureSetting("gbtRetryErroredQueries") && this._queryRefresher.canRetryErroredQueries();
            // A target is retriable only while the refresher will re-run it and
            // it hasn't exhausted its bounded retries. Everything else is
            // "exhausted" and settles to the empty result.
            const isRetriable = (t: ActiveQuery) =>
                canRetryErroredQueries && t.errorRetryCount < maxErroredQueryRetries;
            const retriableTargets = targets.filter(isRetriable);
            const exhaustedTargets = targets.filter(t => !isRetriable(t));

            // FIXME: Shouldn't we be sending a signal to consumers somehow?
            for (const target of retriableTargets) {
                target.error = firstQueryBody;
                this.finishQuery(target);
                // These are already finished as errored, so keep the response
                // loop below (and its "backend did not respond" fallback) from
                // touching them again.
                queriesByID.delete(target.subQueryID);
            }

            if (exhaustedTargets.length === 0) {
                // Every target is being retried; nothing settles to empty.
                return;
            }

            // The exhausted targets settle to empty by flowing synthesized
            // empty-success responses through the normal handling below.
            firstQueryBody = {
                responses: exhaustedTargets.map(t => ({
                    queryID: t.subQueryID,
                    kind: "success",
                    rows: [],
                    table: defined(this._table),
                })),
            };
        }

        // if (Math.random() < 0.5) {
        //     logInfo("Simulating BigQuery error");
        //     return setErrorMessage("Simulated BigQuery error");
        // }

        let again = false;
        for (const response of firstQueryBody.responses) {
            const { queryID } = response;
            const receiver = queriesByID.get(queryID);
            if (receiver === undefined) {
                logError("No receiver for query", queryID);
                continue;
            }

            if (response.kind === "error") {
                queriesByID.delete(queryID);
                receiver.error = new ErrorInfo(400, response.message);
                this.finishQuery(receiver);
            } else if (response.kind === "success") {
                if (this._table === undefined) {
                    // We only use the table we get back from the query if
                    // we're desperately in need of it.  It doesn't contain
                    // computed columns, or source metadata, so aggregate
                    // invalidation won't work with it, for example.
                    this.setTable({
                        ...tableGlideTypeCodecTableToTableGlideType(response.table),
                        name: this._tableName,
                    });
                }

                this.updateLatestReceivedVersion(response.version);
                this._rowVersions.update(receiver.subQueryID, receiver.subQuery, response.version);
                setTableVersionsForQuery(receiver, response.tableVersions);

                queriesByID.delete(queryID);
                receiver.error = undefined;
                receiver.errorRetryCount = 0;
                receiver.warningMessage = undefined;
                if (response.numRowsInTable !== undefined) {
                    this._callbacks.setNumberOfRowsInTable(this._tableName, response.numRowsInTable);
                }
                const rowsToAdd = getRowsToAdd(response.rows);
                // We pass `undefined` to signal that we don't know yet
                // whether there will be rows or not.  In that case we should
                // keep the loading state. If `addRows` returns true, we should
                // continue.
                const shouldContinue = this.addRowsToActiveQuery(
                    receiver,
                    rowsToAdd.length > 0 || response.continuation === undefined ? rowsToAdd : undefined
                );

                // If there were no rows, we still need to push dirt if the table had been loading,
                //  otherwise loading spinners are shown indefinitely for Small Tables.
                if (rowsToAdd.length === 0 && this.dataIsLoading) {
                    this.dataIsLoading = false;
                    this.pushDirtForTable();
                }

                if (response.continuation === undefined) {
                    // If this was a row version query, it won't return a continuation. That shouldn't be a problem
                    // because this shouldn't have make a row version query unless it has the full table downloaded
                    // which means there was no next continuation to begin with.
                    receiver.nextContinuation = undefined;
                    this.finishQuery(receiver);
                    receiver.lastValue = undefined;
                } else {
                    // FIXME: What do we do about local clock skew?
                    const expiresAfter = new Date(
                        response.continuation?.expirationTimestampMS ?? Date.now() + defaultContinuationExpiration
                    );
                    receiver.nextContinuation = {
                        token: response.continuation,
                        expiresAfter,
                    };

                    // FIXME: Why don't we use `finishQuery` here if
                    // `!shouldContinue`?  Don't we want all the stuff it
                    // does?
                    receiver.state = shouldContinue ? QueryState.Continue : QueryState.Done;
                    if (!shouldContinue) {
                        receiver.lastValue = undefined;
                    }
                    this.onAfterQuery(receiver);

                    // FIXME: shouldn't this be `shouldContinue`, and not `true`?
                    again = true;
                }
            } else {
                return assertNever(response);
            }
        }

        // These queries didn't receive any continuations or rows. Assume they're done.
        for (const receiver of queriesByID.values()) {
            logError("Backend did not respond with subquery", receiver.subQueryID);
            receiver.error = undefined;
            receiver.warningMessage = undefined;
            this.addRowsToActiveQuery(receiver, []);
            this.finishQuery(receiver);
        }

        this.updateQueriesRunningWatchable();

        if (again) {
            this._serviceExistingQueriesJob.request();
        } else if (targetState === QueryState.Execute) {
            // We might still have other "executing" queries with callbacks.
            this._serviceNewQueriesJob.request();
        }

        return;
    }

    private requestExistingQueryContinuation = async (
        targets: readonly ActiveQuery[]
    ): Promise<ErrorOr<ExecuteQueryResponseBody>> => {
        const requests = mapFilterUndefined(targets, t =>
            t.nextContinuation === undefined
                ? undefined
                : {
                      queryID: t.subQueryID,
                      continuation: t.nextContinuation?.token,
                      minVersion: this._latestReceivedVersion,
                      limit: t.subQuery.limit,
                  }
        );
        return await this._backendCaller.requestContinueQuery(
            this._appEnvironment,
            this._savedID,
            this._builderInfo,
            requests
        );
    };

    private async serviceExistingQueries() {
        await this.serviceQueriesInternal(QueryState.Continue, this.requestExistingQueryContinuation);
        this.scheduleGC();
    }

    private _serviceExistingQueriesJob = new RecurrentBackgroundJob(() => this.serviceExistingQueries());

    private requestNewQueries = async (targets: readonly ActiveQuery[]) => {
        const requests = targets.map(target => {
            const { subQuery, subQueryID, maxLimit, nextContinuation } = target;
            const endOfTable = nextContinuation === undefined;
            const rowVersionQuery = this._rowVersions.makeQuery(subQueryID, subQuery, endOfTable);
            // `target.currentValue` can be a loading value if the query is
            // still being continued.  It can also just be a table, in the
            // case where a query that has finished is re-queried with an
            // increased limit.
            if (
                rowVersionQuery.rowVersion === undefined &&
                isLoadingValue(target.currentValue) &&
                getFeatureSetting("gbtFixRefreshDeletes")
            ) {
                // ##gbtFixRefreshDeletes:
                // `lastValue` is what will be returned until the query has
                // been answered by the backend.  We must reset
                // `currentValue`, or else the incoming rows will just be
                // added here, and rows will never be removed, even if the
                // backend doesn't return them anymore.  If we're querying
                // only the changes since `rowVersion`, then `currentValue`
                // will be updated with the changes.  In that case it will
                // have the current rows as the display value.
                target.currentValue = makeLoadingValue();
            }
            const adjustedLimitQuery = adjustRequeryLimit(rowVersionQuery, maxLimit);

            return makeQueryRequest(
                subQueryID,
                adjustedLimitQuery,
                this._latestReceivedVersion,
                this._appEnvironment.authenticator.appUserID
            );
        });
        return this._backendCaller.requestExecuteQuery(
            this._appEnvironment,
            this._savedID,
            this._builderInfo,
            requests
        );
    };

    private async serviceNewQueries() {
        await this.serviceQueriesInternal(QueryState.Execute, this.requestNewQueries);
        this.scheduleGC();
    }

    private async serviceNewAggregations() {
        for (const q of this._activeAggregationQueries.values()) {
            if (q.state !== QueryState.Execute) continue;
            void this.runAggregationQuery(q);
        }
        this.updateQueriesRunningWatchable();
    }

    private async runAggregationQuery(target: ActiveAggregationQuery): Promise<void> {
        const responseBody = await this._aggregateAggregator.requestExecuteQuery(target.subQuery, target.subQueryID);

        // FIXME: It's possible that at this point `target` is not an active
        // query anymore because it's been invalidated itself.  In that case
        // it should not remove the invalidated query here.  See this Replay
        // for a reproduction of this bug:
        // https://legacy.replay.io/recording/why-does-rollup-disappear--73d96247-2790-4243-91ec-08de75159d4e
        this._invalidatedAggregateQueries.delete(target.subQuery);

        if (isErrorInfo(responseBody)) {
            target.error = responseBody;
        } else {
            assert(responseBody.responses.length <= 1);
            const response = responseBody.responses[0];
            if (response === undefined) {
                target.error = new ErrorInfo(500, "No response");
            } else if (response.kind === "error") {
                target.error = new ErrorInfo(400, response.message);
            } else {
                let rowIndex = nativeTableIndexer.zero;
                const rows = getRowsToAdd(response.rows).map(r => {
                    const rowID = this._appEnvironment.appFacilities.makeRowID();
                    const row = makeRowFromRowData(undefined, rowID, r, rowIndex);
                    rowIndex = nativeTableIndexer.nextNumber(rowIndex);
                    return row;
                });
                target.currentValue = new MutableTable(rows);
                target.lastValue = undefined;
                target.error = undefined;
                target.errorRetryCount = 0;
                setTableVersionsForQuery(target, response.tableVersions);
            }
        }

        this.finishQuery(target);
        this.updateQueriesRunningWatchable();
    }

    private _serviceNewQueriesJob = new RecurrentBackgroundJob(() => this.serviceNewQueries());
    private _serviceNewAggregationsJob = new RecurrentBackgroundJob(() => this.serviceNewAggregations());

    private doesQueryUseComputedColumns(q: SerializedQuery): boolean {
        const columnNames = getColumnsUsedByQuery(q);
        const hasComputed = this._table?.columns.some(c => isComputedColumn(c) && columnNames.has(c.name)) === true;
        return hasComputed;
    }

    private get latestReceivedVersionForOverlaying(): number {
        if (typeof this._latestReceivedVersion === "number") {
            return this._latestReceivedVersion;
        } else {
            return 0;
        }
    }

    private addRowsToActiveQuery(active: ActiveQuery, rows: readonly JSONObject[] | undefined) {
        const { subQuery, subQueryID } = active;

        // It's important that we bail if we can't find our own query
        // anymore.  The GC, for example, will remove queries that
        // aren't in use anymore.
        const maybeOther = this.getActiveQuery(subQuery);
        if (maybeOther === undefined || maybeOther.subQueryID !== subQueryID) return false;

        let shouldContinue: boolean;
        if (rows !== undefined) {
            const queriesToUpdate = new Set<SerializedQuery>();
            active.currentValue = unwrapLoadingTable(active.currentValue);

            for (const rowData of rows) {
                const rowID = makeRowIDForRow(this._table, rowData, this._appEnvironment.appFacilities);
                const existingRow = this.tableData.get(rowID);
                const existingRowIndex = definedMap(existingRow, getRowIndex);
                const rowIndexFallback = existingRowIndex ?? this._callbacks.makeRowIndexFallback();

                let row = makeRowFromRowData(this._table, rowID, rowData, rowIndexFallback);
                const withChanges = this._rowChangeOverlayer.overlayChangesToRow(
                    row,
                    this.latestReceivedVersionForOverlaying
                );

                if (withChanges === undefined) {
                    // The row has been deleted locally
                    if (existingRow !== undefined) {
                        this.deleteRow(row.$rowID);
                    }
                    continue;
                } else {
                    // This row was deleted from the database
                    if (withChanges[nativeTableRowDeletedColumnName] === true) {
                        this.deleteRow(row.$rowID);
                        this.deleteRowFromActiveQueries(row.$rowID);
                        continue;
                    }
                }

                if (existingRow !== undefined) {
                    this.updateRowData(existingRow, withChanges, false, false, false);
                    row = existingRow;
                } else {
                    this.addRow(withChanges, queriesToUpdate);
                    this._numRowsAddedSinceGC++;
                    row = withChanges;
                }
                // TODO: `updateRowData` and `addRow` should already
                // have added the row to this query, so this should be
                // superfluous.  We should make sure that's the case
                // and then maybe change this to an `assert`.
                active.currentValue.set(row.$rowID, row);
            }

            active.currentValue = active.currentValue.withError(active.error);
            this.sortRowsIfNecessary(active);

            const toCallBack: ActiveQuery[] = [];
            for (const [serializedQuery, activeQuery] of this._activeQueries) {
                if (!queriesToUpdate.has(serializedQuery)) continue;

                this.sortRowsIfNecessary(activeQuery);
                toCallBack.push(activeQuery);
            }

            for (const activeQuery of toCallBack) {
                runAndClearCallbacks(activeQuery);
            }

            shouldContinue = active.currentValue.size < active.maxLimit;
        } else {
            shouldContinue = true;
        }

        // FIXME: Do we really need to run callbacks if `rows` is `undefined`?
        runAndClearCallbacks(defined(active));
        return shouldContinue;
    }

    // If this returns a `MutableTable`, the query is done.
    private getCurrentValueForNewQuery(subQuery: SerializedQuery): MutableTable | LoadingValue {
        if (this._shortCircuitRowIDQueries) {
            const rowID = decomposeRowIDQuery(subQuery, this._table);
            if (rowID !== undefined) {
                const row = this.tableData.get(rowID);
                if (row !== undefined) {
                    return new MutableTable([row]);
                }
            }
        }

        const addedRows = this._rowChangeOverlayer.getAddedRows();
        const columns = this._table?.columns ?? [];
        const matchingRows = mapFilterUndefined(addedRows, r => {
            const row = this.tableData.get(r.$rowID);
            if (row === undefined) return undefined;
            if (!areConditionsTrueForRow(subQuery, cn => row[cn], columns)) return undefined;
            return row;
        });
        if (matchingRows.length > 0) {
            return makeLoadingValueWithDisplayValue(new MutableTable(matchingRows));
        } else {
            return makeLoadingValue();
        }
    }

    public runQuery(subQuery: SerializedQuery, callback: () => void): Table | LoadingValue {
        const getCurrentValueOrClosest = <T extends boolean>(
            active: T extends true ? ActiveAggregationQuery : ActiveQuery,
            isAggregation: T
        ) => {
            if (isLoadingValue(active.currentValue)) {
                // FIXME: if `active.currentValue` has a display value,
                // shouldn't we just return it?
                const closest = this.getClosestResolvedOrInvalidatedQuery(
                    subQuery,
                    isAggregation ? this._activeAggregationQueries : this._activeQueries
                );
                if (closest !== undefined) {
                    return closest;
                }
            }

            if (active.lastValue !== undefined) {
                return makeLoadingValueWithDisplayValue(active.lastValue);
            }

            return active.currentValue;
        };

        if (subQuery.groupBy !== undefined) {
            let active = this._activeAggregationQueries.get(subQuery);

            if (active !== undefined) {
                active.callbacks.add(callback);
                return getCurrentValueOrClosest(active, true);
            }

            active = {
                subQuery,
                subQueryID: this._appEnvironment.appFacilities.makeUUID(),
                callbacks: new Set([callback]),
                state: QueryState.Execute,
                currentValue: makeLoadingValue(),
                lastValue: undefined,
                error: undefined,
                errorRetryCount: 0,
                warningMessage: undefined,
                updateLocally: !this.doesQueryUseComputedColumns(subQuery),
                tableVersions: undefined,
            };
            this._activeAggregationQueries.set(subQuery, active);
            void this.runAggregationQuery(active);
            this.updateQueriesRunningWatchable();

            return getCurrentValueOrClosest(active, true);
        }

        const subQueryLimit = subQuery.limit ?? Number.MAX_SAFE_INTEGER;

        const finish = (job: RecurrentBackgroundJob) => {
            this._activeQueriesCV.notifyAll();
            job.request();
            this.updateQueriesRunningWatchable();

            return getCurrentValueOrClosest(defined(active), false);
        };

        let active = this.getActiveQuery(subQuery);
        let currentValue: MutableTable | LoadingValue;
        let state = QueryState.Execute;
        if (active !== undefined) {
            // We always add the callback, even if the query is already done.
            // We do that to keep track of which queries are still needed:
            // queries that don't have callbacks aren't in use anymore and we
            // can GC them.
            active.callbacks.add(callback);

            // This is where we use a continuation and increase the limit
            // instead of making a new query.  We only do this if the query is
            // still on the same serial and the continuation hasn't expired
            // yet.
            if (
                active.state === QueryState.Done &&
                active.saveSerial === this._saveSerial &&
                active.maxLimit < subQueryLimit &&
                active.nextContinuation !== undefined &&
                active.nextContinuation.expiresAfter > new Date()
            ) {
                // Note that we're using `maxLimit` here, vs
                // `currentValue.size`. `maxLimit` is the most we've ever
                // queried.  `currentValue.size` can actually be larger than
                // that because we add rows from other queries to it if they
                // match the conditions.  But consider a situation where we
                // query one table from both sides, i.e. one query sorts
                // ascending and the other descending. We start by doing 3
                // rows for each, so we get A, B, C for the ascending query
                // and X, Y, Z for the descending query.  They use the same
                // condition, however, so we add each queries results to the
                // other query's `currentValue`, so they will now both have 6
                // rows.  Since the `maxLimit` is still 3 we will never return
                // those other rows, but now imagine we increase the limit to
                // 6 but decide that we don't actually need to query because
                // we already have 6 rows.  The ascending query will now
                // return A, B, C, X, Y, Z, when it should actually query for
                // 3 more rows after C and eventually return A, B, C, D, E, F.
                const numRowsMissing = subQueryLimit - active.maxLimit;
                assert(numRowsMissing > 0);

                active.subQuery = { ...active.subQuery, limit: numRowsMissing };
                active.maxLimit = subQueryLimit;
                active.state = QueryState.Continue;

                return finish(this._serviceExistingQueriesJob);
            }

            if (active.state !== QueryState.Done) {
                if (active.state === QueryState.Execute) {
                    this._serviceNewQueriesJob.request();
                } else {
                    this._serviceExistingQueriesJob.request();
                }
            }
            // If the save serial has changed, or the limit has increased, we
            // need to run it again anyway.
            if (active.saveSerial === this._saveSerial) {
                currentValue = active.currentValue;
                if (subQueryLimit <= active.maxLimit) {
                    return getCurrentValueOrClosest(active, false);
                }
            } else {
                currentValue = makeLoadingValue();
            }
        } else {
            currentValue = this.getCurrentValueForNewQuery(subQuery);
            if (!isLoadingValue(currentValue)) {
                state = QueryState.Done;
            }
        }
        const subQueryID = this._appEnvironment.appFacilities.makeUUID();

        active = {
            subQuery,
            subQueryID,
            callbacks: new Set(state === QueryState.Done ? [] : [callback]),
            state,
            // If we have an active query with the same save serial, it had a lower limit
            // than we wanted. We need to preserve the currentValue the previous active
            // query had so that we don't blink in and out of existence.
            currentValue,
            lastValue: undefined,
            warningMessage: undefined,
            error: undefined,
            errorRetryCount: 0,
            tableVersions: undefined,
            nextContinuation: undefined,
            saveSerial: this._saveSerial,
            maxLimit: subQueryLimit,
            updateLocally: !this.doesQueryUseComputedColumns(subQuery),
        };

        if (state === QueryState.Done) {
            this.setActiveQuery(subQuery, active);
            return active.currentValue;
        }

        // If we initialized `currentValue` with rows that were added by
        // actions, they need to be sorted.
        this.sortRowsIfNecessary(active);
        this.setActiveQuery(subQuery, active);

        return finish(this._serviceNewQueriesJob);
    }

    public resetAllQueries(version: number | undefined, appUserChanged: boolean) {
        this.updateLatestReceivedVersion(version);

        if (appUserChanged) {
            this._rowChangeOverlayer.clear();

            if (
                isGlideTableInGBTDataStore(this._table?.sourceMetadata) &&
                this._appEnvironment.isPlayerInBuilder &&
                this._table?.emailOwnersColumn !== undefined &&
                getFeatureSetting("gbtClearSmallTablesOnAppUserChange")
            ) {
                // Reset table contents so row-owner filtered tables don't retain rows from the previous user.
                this.clear();
                this.pushDirtForTable();
            }
        }

        // Minimize callbacks we need to call
        const callbacks = new Set<() => void>();

        for (const q of this._activeQueries.values()) {
            setUnionInto(callbacks, q.callbacks);
        }
        this._activeQueries.clear();

        for (const q of this._activeAggregationQueries.values()) {
            setUnionInto(callbacks, q.callbacks);
            if (!isLoadingValue(q.currentValue)) {
                this._invalidatedAggregateQueries.set(q.subQuery, q.currentValue);
            }
        }
        this._activeAggregationQueries.clear();

        this.updateQueriesRunningWatchable();

        // We call them after deleting the queries, just in case they call us
        // back synchronously.
        for (const cb of callbacks) {
            cb();
        }
    }

    // This is `public` only for testing
    public separateUserSpecificValues(values: Record<string, WritableValue>) {
        // This is to support user-specific values when no user is logged in, in a way that's compatible with Glide
        // Tables. In that case the backend won't persist them, so we have to make sure that we preserve them in memory.
        // We do that by saving them separately in the row change overlayer, in such a way that they're never confirmed
        // or rejected, which we do by giving them a dummy job ID.
        if (
            getFeatureSetting("gbtSupportUserSpecificWithoutUser") &&
            this._table !== undefined &&
            !this._callbacks.getCanSaveUserSpecificColumns()
        ) {
            // We always return user agnostic values, even if they're empty.  That's
            // because the row change overlayer will complain if we "obsolete" a job
            // that doesn't exist.
            const userAgnosticValues: Record<string, WritableValue> = {};
            let userSpecificValues: Record<string, WritableValue> | undefined;
            for (const column of this._table.columns) {
                const v = values[column.name];
                if (v === undefined) continue;
                if (column.isUserSpecific === true) {
                    if (userSpecificValues === undefined) {
                        userSpecificValues = {};
                    }
                    userSpecificValues[column.name] = v;
                } else {
                    userAgnosticValues[column.name] = v;
                }
            }
            return { userAgnosticValues, userSpecificValues };
        } else {
            return { userAgnosticValues: values, userSpecificValues: undefined };
        }
    }

    public addRow(row: Row, queriesToUpdate?: Set<SerializedQuery>): void {
        super.addRow(row);

        const toCallBack: ActiveQuery[] = [];

        for (const [serializedQuery, active] of this._activeQueries.entries()) {
            if (!active.updateLocally) continue;

            if (serializedQuery.groupBy !== undefined) continue;

            const columns = this._table?.columns ?? [];
            if (!areConditionsTrueForRow(serializedQuery, cn => row[cn], columns)) continue;

            if (isLoadingValue(active.currentValue)) {
                let rows: Row[];
                const displayValue = unwrapLoadingValue(active.currentValue);
                if (!isLoadingValue(displayValue) && isTable(displayValue)) {
                    rows = Array.from(displayValue.asMutatingArray());
                } else {
                    rows = [];
                }
                rows.push(row);
                active.currentValue = makeLoadingValueWithDisplayValue(new MutableTable(rows));
            } else {
                active.currentValue.set(row.$rowID, row);
                if (queriesToUpdate === undefined) {
                    // We need to preserve the sort, if there is one
                    this.sortRowsIfNecessary(active);
                }
            }

            toCallBack.push(active);

            queriesToUpdate?.add(serializedQuery);
        }

        if (queriesToUpdate === undefined) {
            for (const active of toCallBack) {
                runAndClearCallbacks(active);
            }
        }
    }

    public addRowFromAction(row: Row, jobID: string): void {
        const { userSpecificValues } = this.separateUserSpecificValues(extractActionValues(row, undefined));

        this._rowChangeOverlayer.addRow(row, this.latestReceivedVersionForOverlaying, jobID);
        if (userSpecificValues !== undefined) {
            // If we have user-specific values that are overlayed just in-memory, but not persisted, they need their own
            // job ID, so we have to set them separately, after adding the row.
            this._rowChangeOverlayer.setColumnsInRow(
                row,
                this.latestReceivedVersionForOverlaying,
                userSpecificValues,
                makeDummyJobID()
            );
        }

        this._columnsAffectedByJob.set(jobID, true);

        this.addRow(row);
    }

    // This should never be called from outside
    public setColumnsInRow(rowIndex: RowIndex, values: Record<string, WritableValue>): void {
        super.setColumnsInRow(rowIndex, values);

        const toCallBack = new Set<ActiveQuery>();

        for (const row of this.findRows(rowIndex)) {
            for (const [serializedQuery, active] of this._activeQueries.entries()) {
                if (!active.updateLocally) continue;

                if (serializedQuery.groupBy !== undefined) continue;

                let currentTable: MutableTable;
                if (getFeatureSetting("gbtAlsoUpdateInLoadingTables")) {
                    // Even if this is just a display value, we still can't
                    // miss deleting a non-matching row from the result set,
                    // because when we re-query, we don't actually delete them
                    // from the result set, even it's just a display value.
                    // That's probably the cause for more of these race
                    // conditions:
                    // https://github.com/glideapps/glide/pull/34441
                    if (isLoadingValue(active.currentValue)) {
                        const unwrapped = unwrapLoadingValue(active.currentValue);
                        if (!(unwrapped instanceof MutableTable)) continue;
                        currentTable = unwrapped;
                    } else {
                        currentTable = active.currentValue;
                    }
                } else {
                    if (isLoadingValue(active.currentValue)) continue;
                    currentTable = active.currentValue;
                }

                const columns = this._table?.columns ?? [];
                const shouldBeIncluded = areConditionsTrueForRow(serializedQuery, cn => row[cn], columns);
                // If it wasn't included, and still shouldn't be, we're good,
                // otherwise we have to do stuff.
                if (!shouldBeIncluded && !currentTable.has(row.$rowID)) continue;

                if (shouldBeIncluded) {
                    currentTable.set(row.$rowID, row);
                    this.sortRowsIfNecessary(active);
                } else {
                    currentTable.delete(row.$rowID);
                }
                toCallBack.add(active);
            }
        }

        for (const active of toCallBack) {
            runAndClearCallbacks(active);
        }
    }

    public setColumnsInRowFromAction(rowIndex: RowIndex, values: Record<string, WritableValue>, jobID: string): void {
        const { userAgnosticValues, userSpecificValues } = this.separateUserSpecificValues(values);

        const rows = this.findRows(rowIndex);
        if (rows.length > 0) {
            for (const row of rows) {
                this._rowChangeOverlayer.setColumnsInRow(
                    row,
                    this.latestReceivedVersionForOverlaying,
                    userAgnosticValues,
                    jobID
                );
                if (userSpecificValues !== undefined) {
                    this._rowChangeOverlayer.setColumnsInRow(
                        row,
                        this.latestReceivedVersionForOverlaying,
                        userSpecificValues,
                        makeDummyJobID()
                    );
                }
            }
        } else {
            const { keyColumnName, keyColumnValue } = decomposeRowIndex(rowIndex);
            if (keyColumnName === nativeTableRowIDColumnName && typeof keyColumnValue === "string") {
                this._rowChangeOverlayer.setColumnsInRow(
                    keyColumnValue,
                    this.latestReceivedVersionForOverlaying,
                    userAgnosticValues,
                    jobID
                );
                if (userSpecificValues !== undefined) {
                    this._rowChangeOverlayer.setColumnsInRow(
                        keyColumnValue,
                        this.latestReceivedVersionForOverlaying,
                        userSpecificValues,
                        makeDummyJobID()
                    );
                }
            }
        }

        // TODO: We'll need the full schema when we support computed columns
        // spanning more than one table.
        const schema = makeSimpleSchemaInspector(makeTypeSchema(filterUndefined([this._table])), undefined, undefined);
        this._columnsAffectedByJob.set(
            jobID,
            getColumnsAffectedByDataColumns(schema, this._table, Object.keys(values), true, true)
        );

        this.setColumnsInRow(rowIndex, values);
    }

    private deleteRowFromActiveQueries(rowID: string): readonly ActiveQuery[] {
        const toCallBack: ActiveQuery[] = [];
        for (const active of this._activeQueries.values()) {
            if (isLoadingValue(active.currentValue)) continue;

            if (active.currentValue.has(rowID)) {
                active.currentValue.delete(rowID);
                toCallBack.push(active);
            }
        }
        return toCallBack;
    }

    public deleteRowAtIndex(rowIndex: RowIndex): void {
        const toCallBack: ActiveQuery[] = [];

        for (const row of this.findRows(rowIndex)) {
            this._rowChangeOverlayer.deleteRow(row.$rowID);

            toCallBack.push(...this.deleteRowFromActiveQueries(row.$rowID));
        }

        // We need to call this here, as opposed to be at the top, because
        // otherwise `findRows` wouldn't find the rows anymore.
        super.deleteRowAtIndex(rowIndex);

        for (const active of toCallBack) {
            runAndClearCallbacks(active);
        }
    }

    public deleteRowAtIndexFromAction(rowIndex: RowIndex, jobID: string): void {
        this.deleteRowAtIndex(rowIndex);
        this._columnsAffectedByJob.set(jobID, true);
    }

    private invalidateQueriesAffectedByColumns(
        activeQueries: ArrayMap<SerializedQuery, ActiveQueryBase>,
        columnsAffected: ReadonlySet<string> | true,
        receivedVersion: number | undefined
    ): void {
        if (columnsAffected !== true && columnsAffected.size === 0) return;

        const queriesToInvalidate: [SerializedQuery, ActiveQueryBase][] = [];
        const callbacks = new Set<() => void>();
        activeQueries.filterInPlace((active, query) => {
            if (columnsAffected !== true && !doesQueryReferToColumns(query, columnsAffected)) {
                return true;
            }

            queriesToInvalidate.push([query, active]);
            setUnionInto(callbacks, active.callbacks);
            return false;
        });

        if (queriesToInvalidate.length === 0) {
            assert(callbacks.size === 0);
            return;
        }

        this.updateLatestReceivedVersion(receivedVersion);

        if (activeQueries === this._activeAggregationQueries) {
            for (const [subQuery, active] of queriesToInvalidate) {
                const currentValue = unwrapLoadingValue(active.currentValue);
                if (!isLoadingValue(currentValue)) {
                    assert(currentValue instanceof MutableTable);
                    this._invalidatedAggregateQueries.set(subQuery, currentValue);
                }
            }
        }

        this.updateQueriesRunningWatchable();

        for (const cb of callbacks) {
            cb();
        }
    }

    public confirmAction(rowID: string | undefined, jobID: string, version: number): void {
        if (rowID !== undefined) {
            const maybeChangedRow = this._rowChangeOverlayer.confirmAction(rowID, jobID, version);
            if (maybeChangedRow !== undefined) {
                const row = this.tableData.get(rowID);
                if (row !== undefined) {
                    this.updateRowData(row, maybeChangedRow, false, false, true);
                }
            }
        }

        const columnsAffected = this._columnsAffectedByJob.get(jobID);
        if (columnsAffected !== undefined) {
            this._columnsAffectedByJob.delete(jobID);
            this.invalidateQueriesAffectedByColumns(this._activeAggregationQueries, columnsAffected, version);
        }
    }

    public rollbackAction(rowID: string, jobID: string): void {
        const maybeChangedRow = this._rowChangeOverlayer.rollbackAction(rowID, jobID);
        if (maybeChangedRow === undefined) return;

        if (maybeChangedRow === "delete") {
            const toCallBack = this.deleteRowFromActiveQueries(rowID);

            // We need to call this here, as opposed to be at the top, because
            // otherwise `findRows` wouldn't find the rows anymore.
            this.deleteRow(rowID);

            for (const active of toCallBack) {
                runAndClearCallbacks(active);
            }
        } else {
            const row = this.tableData.get(rowID);
            if (row !== undefined) {
                this.updateRowData(row, maybeChangedRow, false, false, true);
            }

            // FIXME: don't we have to update local queries here?
        }
    }

    public obsoleteAction(jobIDA: string, jobIDB: string): void {
        this._rowChangeOverlayer.obsoleteAction(jobIDA, jobIDB);
    }

    public tableVersions(): Record<string, QueryTableVersions> {
        return this._queryRefresher.tableVersions();
    }

    public retriableErroredQueryIDs(): Set<string> {
        return this._queryRefresher.retriableErroredQueryIDs();
    }

    public canRetryErroredQueries(): boolean {
        return this._queryRefresher.canRetryErroredQueries();
    }

    public requery(queryIDs: Set<string>): void {
        return this._queryRefresher.requery(queryIDs);
    }

    public onAfterQuery(query: ActiveQueryBase): void {
        return this._queryRefresher.onAfterQuery(query);
    }
}

export function forceLoadQueryableTable(
    tableName: TableName,
    ds: ExistingQueryRowDataStoreImpl | QueryableDataStore,
    limit: number,
    forBuilder: boolean
): GroundValue {
    const query = new Query(tableName).withLimit(limit);
    let keepRunning = true;

    // Callbacks are cleared when the query returns. Need to rerun the query in the callback to ensure a query for
    // this table is always active. Otherwise, live updates or the reload button doesn't work. This won't kick off
    // a new query it will simply add a callback to the existing query's callbacks set.
    const recursivelyRunQuery: () => GroundValue = () => {
        const result =
            ds instanceof ExistingQueryRowDataStoreImpl
                ? ds.runQuery(query.serialize(), keepRunning ? recursivelyRunQuery : () => undefined)
                : ds.fetchQuery(query, recursivelyRunQuery, forBuilder);
        if (getFeatureSetting("gbtDontRetrySmallTables404")) {
            const unwrapped = unwrapLoadingValue(result);
            if (!isLoadingValue(unwrapped) && isTable(unwrapped)) {
                if (unwrapped.error !== undefined && unwrapped.error.status === 404) {
                    keepRunning = false;
                }
            }
        }
        return result;
    };

    return recursivelyRunQuery();
}

export function isExistingQueryRowDataStore(ds: unknown): ds is ExistingQueryRowDataStore {
    return ds instanceof ExistingQueryRowDataStoreImpl;
}
